import { NumberFormatter } from '@mantine/core';
import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Input } from 'src/design/atoms/Input';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Radio } from 'src/design/atoms/Radio';
import { Select } from 'src/design/atoms/Select';
import { TextInput } from 'src/design/atoms/TextInput';
import { Textarea } from 'src/design/atoms/Textarea';
import { DateInput } from 'src/design/atoms/DateInput';
import { useFormik } from 'formik';
import React, { useMemo } from 'react';
import { AccountType } from './types';
import { CostCodeSelector } from 'src/routes/Jobs/WBS/components/CostCodeSelector';
import { useJobs } from 'src/hooks/useJobs';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { hasProgressManagedDescendant } from 'src/utils';
import * as Yup from 'yup';
import { CTCLinkIndicator } from './CTCLinkIndicator';
import { ProgressManagedIndicator } from './ProgressManagedIndicator';
import { useWbs } from 'src/routes/Jobs/WBS/contexts/WbsContext';

const baseValidationSchema = Yup.object().shape({
  costCodeId: Yup.string().nullable(),
  description: Yup.string(),
  active: Yup.boolean().required(),
  timesheetsOnly: Yup.boolean().required(),
  perDiemEligible: Yup.boolean().required(),
  scalePayEnabled: Yup.boolean().required(),
  accountType: Yup.string()
    .oneOf(['labor', 'cost', 'other'], 'Invalid account type')
    .required(),
});

const getValidationSchema = (
  accountType: AccountType,
  isRootAccount: boolean,
  isFolder: boolean,
) => {
  if (isFolder) {
    return baseValidationSchema.shape({
      name: Yup.string()
        .required('Name is required')
        .min(1, 'Name must not be empty'),
    });
  }
  if (accountType === 'labor') {
    return baseValidationSchema.shape({
      name: Yup.string().optional(),
      apply: Yup.boolean().required(),
      currentQuantityEstimate: Yup.number()
        .min(0, 'Estimate must be 0 or greater')
        .nullable()
        .optional(),
      initialQuantityEstimate: Yup.number()
        .min(0, 'Estimate must be 0 or greater')
        .nullable()
        .optional(),
      currentQuantityActual: Yup.number()
        .min(0, 'Actual must be 0 or greater')
        .nullable()
        .required(),
      initialHoursEstimate: Yup.number()
        .min(0, 'Estimate must be 0 or greater')
        .optional(),
      currentHoursEstimate: Yup.number()
        .min(0, 'Estimate must be 0 or greater')
        .optional(),
      currentHoursActual: Yup.number()
        .min(0, 'Actual must be 0 or greater')
        .optional(),
      startDate: Yup.date().optional().nullable(),
      finishDate: Yup.date().optional().nullable(),
    });
  } else {
    return baseValidationSchema.shape({
      name: Yup.string().optional(),
      apply: Yup.boolean().required(),
      currentQuantityEstimate: Yup.number()
        .min(0, 'Estimate must be 0 or greater')
        .nullable()
        .optional(),
      initialQuantityEstimate: Yup.number()
        .min(0, 'Estimate must be 0 or greater')
        .nullable()
        .optional(),
      currentQuantityActual: Yup.number()
        .min(0, 'Actual must be 0 or greater')
        .nullable()
        .optional(),
      unitOfMeasure: isRootAccount
        ? Yup.string()
            .oneOf(
              ['hours', '$', 'pieces', 'heads', 'feet', ''],
              'Invalid unit of measure',
            )
            .required()
        : Yup.string().optional(),
      startDate: Yup.date().optional().nullable(),
      finishDate: Yup.date().optional().nullable(),
    });
  }
};

export const EditAccountForm: React.FC = () => {
  const {
    selectedTreeRow,
    onAccountSaved,
    isPlaceholderAccount,
    findRootAncestor,
    collectLeafDescendants,
  } = useWbs();
  const { selectedJob } = useJobs();
  const [error, setError] = React.useState<string | null>(null);
  const [wasCtcConnected, setWasCtcConnected] = React.useState(
    selectedTreeRow.isCtcConnected,
  );

  React.useEffect(() => {
    if (wasCtcConnected && !selectedTreeRow.isCtcConnected) {
      // Account was disconnected from CTC, re-enable editing
      formik.setFieldTouched('currentQuantityEstimate', false);
      formik.setFieldTouched('currentHoursEstimate', false);
      setWasCtcConnected(false);
    } else if (selectedTreeRow.isCtcConnected) {
      setWasCtcConnected(true);
    }
  }, [selectedTreeRow.isCtcConnected]);

  const isFolder =
    selectedTreeRow.children && selectedTreeRow.children.length > 0;

  const isRootAccount =
    selectedTreeRow.parentId === null && !selectedTreeRow.isArtificial;

  const isLeafAccount = selectedTreeRow.isLeaf;

  const hasPmDescendants = useMemo(
    () => hasProgressManagedDescendant(selectedTreeRow),
    [selectedTreeRow],
  );
  // Suffix added to read-only roll-up labels to make exclusion visible.
  const pmExclusionLabelSuffix = hasPmDescendants ? ' (excl. PM)' : '';

  const formik = useFormik({
    initialValues: {
      name: selectedTreeRow.name,
      costCodeId: selectedTreeRow.costCodeId,
      description: selectedTreeRow.description || '',
      accountType: selectedTreeRow.accountType || 'other',
      active: selectedTreeRow.active,
      apply: selectedTreeRow.apply,
      timesheetsOnly: selectedTreeRow.timesheetsOnly,
      perDiemEligible: selectedTreeRow.perDiemEligible ?? false,
      scalePayEnabled: selectedTreeRow.scalePayEnabled ?? false,
      unitOfMeasure: selectedTreeRow.unitOfMeasure,
      startDate:
        selectedTreeRow.startDate &&
        typeof selectedTreeRow.startDate === 'string'
          ? new Date(selectedTreeRow.startDate)
          : selectedTreeRow.startDate,
      finishDate:
        selectedTreeRow.finishDate &&
        typeof selectedTreeRow.finishDate === 'string'
          ? new Date(selectedTreeRow.finishDate)
          : selectedTreeRow.finishDate,
      initialQuantityEstimate: selectedTreeRow.initialQuantityEstimate,
      currentQuantityEstimate: selectedTreeRow.currentQuantityEstimate,
      currentQuantityActual: selectedTreeRow.currentQuantityActual,
      initialHoursEstimate: selectedTreeRow.initialHoursEstimate,
      currentHoursEstimate: selectedTreeRow.currentHoursEstimate,
      currentHoursActual: selectedTreeRow.currentHoursActual,
    },
    validationSchema: getValidationSchema(
      (selectedTreeRow.accountType || 'none') as AccountType,
      isRootAccount,
      isFolder,
    ),
    enableReinitialize: true,
    onSubmit: async (values) => {
      setError(null);
      try {
        const accountUpdates = {
          name: values.name,
          costCodeId: values.costCodeId,
          description: values.description,
          accountType: values.accountType,
          active: values.active,
          apply: values.apply,
          timesheetsOnly: values.timesheetsOnly,
          perDiemEligible: values.perDiemEligible,
          scalePayEnabled: values.scalePayEnabled,
          unitOfMeasure: values.unitOfMeasure,
          startDate: values.startDate,
          finishDate: values.finishDate,
          initialQuantityEstimate: values.initialQuantityEstimate,
          currentQuantityEstimate: values.currentQuantityEstimate,
          currentQuantityActual: values.currentQuantityActual,
          initialHoursEstimate: values.initialHoursEstimate,
          currentHoursEstimate: values.currentHoursEstimate,
          currentHoursActual: values.currentHoursActual,
        };

        await onAccountSaved(selectedTreeRow, accountUpdates);
      } catch (err: any) {
        setError(err.message || 'Failed to save account');
      }
    },
  });

  const isPlaceholder = isPlaceholderAccount(formik.values.apply);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      formik.handleSubmit();
    }
  };

  const rootMetrics = useMemo(() => {
    const rootAccount = findRootAncestor(selectedTreeRow);
    const rootIsCTCConnected = rootAccount && rootAccount.isCtcConnected;

    // Collect all leaf descendants of the root (excluding the root itself).
    let leafDescendants = collectLeafDescendants(rootAccount);

    // Exclude the current account if it's a leaf (we'll use formik value for it)
    if (isLeafAccount) {
      leafDescendants = leafDescendants.filter(
        (node) => node.id !== selectedTreeRow.id,
      );
    }

    // Quantity uses domain units (heads, feet, etc.) that are not summable
    // across progress-managed children, so they are excluded from this total.
    // Hours are uniform across labor accounts and remain summed normally.
    const totalLeafQuantity = leafDescendants
      .filter((node) => !node.progressManaged)
      .reduce((sum, node) => sum + (node.currentQuantityEstimate ?? 0), 0);
    const totalLeafHours = leafDescendants.reduce(
      (sum, node) => sum + Number(node.currentHoursEstimate),
      0,
    );

    // Only show total estimate if this is a child account and root is CTC connected
    const showTotalRootEstimate = !isRootAccount && rootIsCTCConnected;
    const rootQuantity = rootAccount?.currentQuantityEstimate ?? 0;
    const rootHours = Number(rootAccount?.currentHoursEstimate);

    return {
      showTotalRootEstimate,
      rootQuantity,
      rootHours,
      totalLeafQuantity,
      totalLeafHours,
    };
  }, [selectedTreeRow, isLeafAccount]);

  const remainingMetrics = useMemo(() => {
    // We do not include rootMetrics in the dependency array because we must wait for formik to
    // be re-initialized when changing accounts. By the time this has happened, rootMetrics will
    // already be updated.

    // Calculate remaining as root minus sum of all leaf descendants (using
    // formik value for current leaf). When the current leaf is itself
    // progress-managed, its quantity was never part of the total (different
    // units), so do not subtract it. Hours, on the other hand, are summable
    // across all labor leaves and remain subtracted normally.
    const isPmLeaf = isLeafAccount && selectedTreeRow.progressManaged;
    const rootQuantityRemaining =
      rootMetrics.rootQuantity -
      rootMetrics.totalLeafQuantity -
      (isLeafAccount && !isPmLeaf
        ? formik.values.currentQuantityEstimate ?? 0
        : 0);
    const rootHoursRemaining =
      rootMetrics.rootHours -
      rootMetrics.totalLeafHours -
      (isLeafAccount && formik.values.accountType === 'labor'
        ? Number(formik.values.currentHoursEstimate)
        : 0);

    return {
      rootQuantityRemaining,
      rootHoursRemaining,
    };
  }, [
    formik.values.currentQuantityEstimate,
    formik.values.currentHoursEstimate,
    formik.values.accountType,
  ]);

  return (
    <Stack gap="md">
      <Group gap="xs" align="center">
        <Title order={4}>
          Edit {isFolder ? 'Roll-Up Account' : 'Charge Code'}:{' '}
          {selectedTreeRow.name}
        </Title>
        <CTCLinkIndicator node={selectedTreeRow} size="xs" />
        <ProgressManagedIndicator node={selectedTreeRow} size="xs" />
      </Group>

      {selectedTreeRow.progressManaged && (
        <Alert color="blue" title="Progress Managed Account">
          This account's progress is managed by a design model. Quantity
          progress is tracked automatically via the progress viewer.
        </Alert>
      )}

      {!selectedTreeRow.progressManaged && hasPmDescendants && (
        <Alert
          color="gray"
          title="Quantity Roll-Up Excludes Progress-Managed Children"
        >
          Quantity totals exclude progress-managed children because their units
          (heads, feet) cannot be summed with sibling accounts. Hours still roll
          up across all children. Open each design-model account directly to see
          its synced quantity actuals.
        </Alert>
      )}

      <Title order={5}>Status Settings</Title>

      <SimpleGrid cols={2}>
        <Checkbox
          label="Is Active"
          description="Show or hide this account in the job"
          checked={formik.values.active}
          onChange={(e) => formik.setFieldValue('active', e.target.checked)}
          onBlur={formik.handleBlur}
        />
        <Checkbox
          label="Is Placeholder"
          description="This account will be visible but won't count towards job progress"
          checked={!formik.values.apply}
          onChange={(e) => formik.setFieldValue('apply', !e.target.checked)}
          onBlur={formik.handleBlur}
          disabled={selectedTreeRow.progressManaged}
        />
        <Checkbox
          label="Is Timesheets Only"
          description="This account will only be used for timesheets and not for progress tracking. It will not be visible in the Tracker or other progress tracking modules."
          checked={formik.values.timesheetsOnly}
          onChange={(e) =>
            formik.setFieldValue('timesheetsOnly', e.target.checked)
          }
          onBlur={formik.handleBlur}
          disabled={selectedTreeRow.progressManaged}
        />
        <Checkbox
          label="Per Diem Eligible"
          description={
            !isRootAccount
              ? 'Can only be changed at the top level'
              : 'When unchecked, per diem will not appear for this account in timesheets and reports'
          }
          checked={formik.values.perDiemEligible}
          onChange={(e) =>
            formik.setFieldValue('perDiemEligible', e.target.checked)
          }
          onBlur={formik.handleBlur}
          disabled={!isRootAccount}
        />
        <Checkbox
          label="Scale Pay Eligible"
          description={
            !isRootAccount
              ? 'Can only be changed at the top level'
              : 'When unchecked, scale pay will not apply to this account even on scale jobs'
          }
          checked={formik.values.scalePayEnabled}
          onChange={(e) =>
            formik.setFieldValue('scalePayEnabled', e.target.checked)
          }
          onBlur={formik.handleBlur}
          disabled={!isRootAccount}
        />
      </SimpleGrid>

      <Title order={5}>Basic Information</Title>

      <SimpleGrid cols={2}>
        {(!isLeafAccount || isPlaceholder) && (
          <TextInput
            label="Name"
            description="Brief name for this account"
            placeholder="Enter name"
            value={formik.values.name}
            onChange={(e) => formik.setFieldValue('name', e.target.value)}
            onBlur={formik.handleBlur}
            error={formik.touched.name && formik.errors.name}
            required
            onKeyDown={handleKeyDown}
          />
        )}
        {!selectedTreeRow.isCtcConnected && (
          <CostCodeSelector
            value={formik.values.costCodeId}
            onChange={(value) => formik.setFieldValue('costCodeId', value)}
            onBlur={formik.handleBlur}
            error={formik.touched.costCodeId && formik.errors.costCodeId}
            required={isLeafAccount}
            officeId={selectedJob?.serviceOfficeId}
          />
        )}
      </SimpleGrid>

      <Textarea
        label="Description"
        placeholder="Add details about this account"
        value={formik.values.description}
        onChange={(e) => formik.setFieldValue('description', e.target.value)}
        onBlur={formik.handleBlur}
        error={formik.touched.description && formik.errors.description}
        autosize
        minRows={2}
        onKeyDown={handleKeyDown}
      />

      <SimpleGrid cols={2}>
        <Select
          label="Account Type"
          placeholder="Select account type"
          value={formik.values.accountType}
          onChange={(value) => formik.setFieldValue('accountType', value)}
          onBlur={formik.handleBlur}
          error={formik.touched.accountType && formik.errors.accountType}
          data={[
            { value: 'labor', label: 'Labor' },
            { value: 'cost', label: 'Cost' },
            { value: 'other', label: 'Other' },
          ]}
          disabled={!isRootAccount || selectedTreeRow.progressManaged}
          required
          description={
            selectedTreeRow.progressManaged
              ? 'Locked for progress-managed accounts'
              : !isRootAccount
              ? 'Can only be changed at the top level'
              : 'Choose what type of costs this account tracks'
          }
        />
        <TextInput
          label="External WBS ID"
          value={selectedTreeRow.externalWbsId || 'N/A'}
          disabled
          description="External WBS ID is imported from CTC"
        />
      </SimpleGrid>

      <Title order={5}>Progress Settings</Title>
      {isPlaceholder && (
        <Text>
          This is a placeholder account - you cannot track progress here.
        </Text>
      )}
      {!isPlaceholder && (
        <>
          <SimpleGrid cols={3}>
            <NumberInput
              label="Initial Quantity Estimate"
              placeholder="Enter initial quantity estimate"
              value={formik.values.initialQuantityEstimate ?? ''}
              onChange={(value) =>
                formik.setFieldValue('initialQuantityEstimate', value || 0)
              }
              onBlur={formik.handleBlur}
              min={0}
              decimalScale={0}
              disabled
            />
            <NumberInput
              label={`Current Quantity Estimate${
                formik.values.unitOfMeasure
                  ? ` (${formik.values.unitOfMeasure})`
                  : ''
              }${pmExclusionLabelSuffix}`}
              placeholder={
                formik.values.currentQuantityEstimate === null
                  ? '—'
                  : 'Enter current quantity estimate'
              }
              value={formik.values.currentQuantityEstimate ?? ''}
              onChange={(value) =>
                formik.setFieldValue('currentQuantityEstimate', value || 0)
              }
              onBlur={formik.handleBlur}
              min={0}
              decimalScale={2}
              disabled={!isLeafAccount}
            />
            <NumberInput
              label={`Current Quantity Actual${
                formik.values.unitOfMeasure
                  ? ` (${formik.values.unitOfMeasure})`
                  : ''
              }${pmExclusionLabelSuffix}`}
              placeholder={
                formik.values.currentQuantityActual === null
                  ? '—'
                  : 'Enter current quantity actual'
              }
              value={formik.values.currentQuantityActual ?? ''}
              onChange={(value) =>
                formik.setFieldValue('currentQuantityActual', value || 0)
              }
              onBlur={formik.handleBlur}
              min={0}
              decimalScale={2}
              disabled
            />
            {formik.values.accountType === 'labor' && (
              <>
                <NumberInput
                  label="Initial Hours Estimate"
                  placeholder="Enter initial hours estimate"
                  value={formik.values.initialHoursEstimate}
                  onChange={(value) =>
                    formik.setFieldValue('initialHoursEstimate', value || 0)
                  }
                  onBlur={formik.handleBlur}
                  min={0}
                  decimalScale={0}
                  disabled
                />
                <NumberInput
                  label="Current Hours Estimate"
                  placeholder="Enter current hours estimate"
                  value={formik.values.currentHoursEstimate}
                  onChange={(value) =>
                    formik.setFieldValue('currentHoursEstimate', value || 0)
                  }
                  onBlur={formik.handleBlur}
                  min={0}
                  decimalScale={0}
                  disabled={!isLeafAccount || selectedTreeRow.isCtcConnected}
                />
                <NumberInput
                  label="Current Hours Actual"
                  placeholder="Enter current hours actual"
                  value={formik.values.currentHoursActual}
                  onChange={(value) =>
                    formik.setFieldValue('currentHoursActual', value || 0)
                  }
                  onBlur={formik.handleBlur}
                  min={0}
                  decimalScale={0}
                  disabled
                />
              </>
            )}
          </SimpleGrid>
          {remainingMetrics &&
            rootMetrics &&
            rootMetrics.showTotalRootEstimate && (
              <>
                <Group gap="xs">
                  <Text size="xs">Total Estimate Remaining from CTC:</Text>
                  <Text
                    size="xs"
                    c={
                      remainingMetrics.rootQuantityRemaining < 0
                        ? 'red'
                        : 'dimmed'
                    }
                  >
                    Quantity:{' '}
                    <NumberFormatter
                      value={remainingMetrics.rootQuantityRemaining}
                    />
                    /<NumberFormatter value={rootMetrics.rootQuantity} />
                  </Text>
                  {formik.values.accountType === 'labor' && (
                    <Text
                      size="xs"
                      c={
                        remainingMetrics.rootHoursRemaining < 0
                          ? 'red'
                          : 'dimmed'
                      }
                    >
                      Hours:{' '}
                      <NumberFormatter
                        value={remainingMetrics.rootHoursRemaining}
                      />
                      /<NumberFormatter value={rootMetrics.rootHours} />
                    </Text>
                  )}
                </Group>
              </>
            )}
          {formik.values.accountType !== 'labor' && (
            <Input.Wrapper label="Unit of Measure" required>
              <Radio.Group
                value={formik.values.unitOfMeasure}
                onChange={(value) =>
                  formik.setFieldValue('unitOfMeasure', value)
                }
                onBlur={formik.handleBlur}
                description={
                  !isRootAccount
                    ? 'Can only be changed at the top level'
                    : 'Choose how to measure work in this account'
                }
              >
                <Group mt="xs">
                  <Radio
                    value="hours"
                    label="Hours"
                    disabled={!isRootAccount}
                  />
                  <Radio value="$" label="$" disabled={!isRootAccount} />
                  <Radio
                    value="pieces"
                    label="Pieces"
                    disabled={!isRootAccount}
                  />
                  <Radio value="" label="None" disabled={!isRootAccount} />
                </Group>
              </Radio.Group>
            </Input.Wrapper>
          )}
          <SimpleGrid cols={2}>
            <DateInput
              label="Start Date"
              value={formik.values.startDate}
              onChange={(value) => formik.setFieldValue('startDate', value)}
              onBlur={formik.handleBlur}
              description={
                isLeafAccount
                  ? 'Start date of the charge code work.'
                  : 'The earliest start date of the charge codes in this account.'
              }
              disabled={!isLeafAccount}
            />
            <DateInput
              label="Finish Date"
              value={formik.values.finishDate}
              onChange={(value) => formik.setFieldValue('finishDate', value)}
              onBlur={formik.handleBlur}
              description={
                isLeafAccount
                  ? 'Finish date of the charge code work.'
                  : 'The latest finish date of the charge codes in this account.'
              }
              disabled={!isLeafAccount}
            />
          </SimpleGrid>
        </>
      )}

      {error && (
        <Alert color="red" title="Error">
          {error}
        </Alert>
      )}

      <Group justify="flex-end" mt="md">
        <SaveButton
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || !formik.dirty}
          loading={formik.isSubmitting}
        >
          Save {isFolder ? 'Roll-Up Account' : 'Charge Code'}
        </SaveButton>
      </Group>
    </Stack>
  );
};
